import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useSize from '@react-hook/size';
import { Spin } from 'antd';

import { convertValueUnit } from '@metservicedev/cf-units';
import { metIntegration } from '@metservicedev/platforms-services-interface';
import { AirportDialSettings, IAirportDialData, IOneMinuteObservation } from '@mono/types/src/weather-widgets/airport-dial-settings';
import { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import { IssueTime } from '@mono/metui';
import { toHash } from '@mono/to-hash';
import { convertDirectionToCardinal } from '@mono/direction-to-cardinal';
import { Dial } from '@mono/dial-component';
import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';
import { usePlatStageContext } from '@mono/use-plat-stage';

import { SwitchSelect } from './switch-select';

const getWindComponents = ({ windSpeed, windDirection, runwayAngle }: { windSpeed?: number; windDirection?: number; runwayAngle?: number }) => {
    if (runwayAngle === undefined || windSpeed === undefined || windDirection === undefined) return { undefined };

    const headwindSpeed = Math.round(Math.abs(windSpeed * Math.cos((windDirection - runwayAngle) * (Math.PI / 180))));
    const crosswindSpeed = Math.round(Math.abs(windSpeed * Math.sin((windDirection - runwayAngle) * (Math.PI / 180))));

    const headWindAngle = calculateWindAngle({ runwayAngle, windDirection });
    const crosswindAngle = calculateWindAngle({ runwayAngle: (runwayAngle + 90) % 360, windDirection });

    return { headwindSpeed, headWindAngle, crosswindSpeed, crosswindAngle };
};

const calculateWindAngle = ({ runwayAngle, windDirection }: { runwayAngle: number; windDirection: number }) => {
    const diff1 = getAngleDifference(windDirection, runwayAngle);
    const diff2 = getAngleDifference(windDirection, runwayAngle + 180);
    const resultAngle = diff1 < diff2 ? runwayAngle : (runwayAngle + 180) % 360;

    return resultAngle;
};

/**
 * Length of a shortest way between two angles. It will be in range [0, 180].
 */
const getAngleDifference = (alpha: number, beta: number) => {
    const phi = Math.abs(beta - alpha) % 360;
    const diff = phi > 180 ? 360 - phi : phi;
    return diff;
};

export const AirportDial = (props: WeatherWidgetRenderProps<AirportDialSettings>) => {
    const { settings, isLoading, theme: themeName } = props;
    const { stage } = usePlatStageContext();

    const chartRef = useRef<HTMLDivElement>(null);
    const [width, height] = useSize(chartRef);

    const { title, timezone, displayTable, datasetId, locationLabel, runwayAngle, units, mode } = settings;

    const isDarkMode = themeName === 'dark';
    const theme = isDarkMode
        ? {
              text: '#ffffff',
              issueTime: '#ffffff',
              bg: '#23262F',
          }
        : {
              text: '#172344',
              issueTime: '#595959',
              bg: '#ffffff',
          };

    const [angleSource, setAngleSource] = useState(localStorage.getItem(`${datasetId}-angleSource`) ?? 'magnetic');
    const [magneticDeclination, setMagneticDeclination] = useState<number>(0);

    const targetTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [data, setData] = useState<IAirportDialData>({
        windSpeed1Min: undefined,
        directionAverage1m: undefined,
        directionAverage10m: undefined,
        directionCwDevMax10m: undefined,
        directionCcwDevMax10m: undefined,
        directionMagneticAverage1m: undefined,
        directionMagneticAverage10m: undefined,
        directionMagneticCwDevMax10m: undefined,
        directionMagneticCcwDevMax10m: undefined,
        issueTime: undefined,
    });

    const { metserviceApi } = metIntegration;

    useMemo(() => {
        if (!datasetId) return;
        const metjwt = localStorage.getItem('metjwt');

        let timeoutHook = 0;
        const abortController = new AbortController();

        const patientQuery = async () => {
            try {
                const headers: {} | { authorization: string } = metjwt
                    ? {
                          authorization: 'Bearer ' + metjwt,
                      }
                    : {};

                await fetch(`${metserviceApi[stage]}/observations/nz/1-minute/weatherStation/${datasetId?.split('.')[1]}/latest`, {
                    headers,
                    signal: abortController.signal,
                })
                    .then(async (res) => {
                        const value = await res.json();

                        const {
                            obs_timestamp,
                            windspd_01mnavg,
                            windccw_10mnmax,
                            windcw__10mnmax,
                            winddir_01mnavg,
                            winddir_10mnavg,
                            wnddirm_01mnavg,
                            wnddirm_10mnavg,
                            wndccwm_10mnmax,
                            wndcwm__10mnmax,
                        } = value.results[0] as IOneMinuteObservation;

                        setData({
                            issueTime: obs_timestamp,
                            windSpeed1Min: windspd_01mnavg,
                            directionCcwDevMax10m: windccw_10mnmax,
                            directionCwDevMax10m: windcw__10mnmax,
                            directionAverage1m: winddir_01mnavg,
                            directionAverage10m: winddir_10mnavg,
                            directionMagneticAverage1m: wnddirm_01mnavg,
                            directionMagneticAverage10m: wnddirm_10mnavg,
                            directionMagneticCcwDevMax10m: wndccwm_10mnmax,
                            directionMagneticCwDevMax10m: wndcwm__10mnmax,
                        });
                    })
                    .catch((e) => {
                        console.warn(e);
                        if (abortController.signal.aborted === true) return;
                    });
            } catch (e) {
                console.error(e);
            } finally {
                timeoutHook = setTimeout(patientQuery, 55 * 1000) as unknown as number;
            }
        };

        patientQuery();

        return () => {
            abortController.abort();
            clearTimeout(timeoutHook);
        };
    }, [toHash(datasetId), stage, metserviceApi]);

    const { avg1m, avg10m, cwDevMax10m, ccwDevMax10m } = useMemo(() => {
        const {
            directionAverage10m,
            directionAverage1m,
            directionCcwDevMax10m,
            directionCwDevMax10m,
            directionMagneticAverage10m,
            directionMagneticAverage1m,
            directionMagneticCcwDevMax10m,
            directionMagneticCwDevMax10m,
        } = data;
        const magDec = [
            { dir: directionAverage1m, mag: directionMagneticAverage1m },
            { dir: directionAverage10m, mag: directionMagneticAverage10m },
            { dir: directionCwDevMax10m, mag: directionMagneticCwDevMax10m },
            { dir: directionCcwDevMax10m, mag: directionMagneticCcwDevMax10m },
        ]
            .map(({ dir, mag }) => {
                if (dir === undefined || mag === undefined) return undefined;
                const diff = Math.abs(dir - mag);
                return diff > 180 ? 360 - diff : diff;
            })
            .filter((v) => v !== undefined)[0];

        magDec !== undefined && setMagneticDeclination(magDec);

        return angleSource === 'true'
            ? { avg1m: directionAverage1m, avg10m: directionAverage10m, cwDevMax10m: directionCwDevMax10m, ccwDevMax10m: directionCcwDevMax10m }
            : {
                  avg1m: directionMagneticAverage1m,
                  avg10m: directionMagneticAverage10m,
                  cwDevMax10m: directionMagneticCwDevMax10m,
                  ccwDevMax10m: directionMagneticCcwDevMax10m,
              };
    }, [angleSource, toHash(data)]);

    const windSpeed = useMemo(() => {
        const { windSpeed1Min } = data;
        if (windSpeed1Min === undefined || units === undefined) return undefined;
        return convertValueUnit({ from: 'knot', to: units, value: windSpeed1Min }) as number;
    }, [data.windSpeed1Min, units]);

    const { crosswindAngle, crosswindSpeed, headWindAngle, headwindSpeed } = useMemo(
        () =>
            getWindComponents({
                windSpeed,
                windDirection: angleSource === 'true' ? data.directionAverage1m : data.directionMagneticAverage1m,
                runwayAngle: runwayAngle * 10 + (angleSource === 'true' ? magneticDeclination : 0),
            }),
        [angleSource, runwayAngle, magneticDeclination, toHash(data)]
    );

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                color: theme.text,
            }}
            ref={chartRef}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <div style={{ fontSize: 16, lineHeight: '22px', fontWeight: 700 }}>{title}</div>
                </div>
                <div style={{ display: 'flex', columnGap: 4 }}>
                    <IssueTime
                        issueTime={data.issueTime}
                        targetTimezone={targetTimezone}
                        isDarkMode={isDarkMode}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            color: theme.issueTime,
                        }}
                    />
                </div>
            </div>
            <Hr style={{ marginTop: 8 }} />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, marginBottom: 8 }}>
                <div style={{ width: 220 }}>
                    <SwitchSelect
                        items={[
                            { label: 'Magnetic', value: 'magnetic' },
                            { label: 'True', value: 'true' },
                        ]}
                        value={angleSource}
                        onChange={(value: string) => {
                            localStorage.setItem(`${datasetId}-angleSource`, value);
                            setAngleSource(value);
                        }}
                        theme={{ bg: theme.bg, themeName: themeName }}
                    />
                </div>
            </div>
            {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                        <Spin style={{ marginTop: 12 }} size='large' />
                    </div>
                </div>
            )}
            {!isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Dial
                            arrowValue={avg1m}
                            dashValue={avg10m}
                            clockWiseDeviation={cwDevMax10m}
                            counterClockWiseDeviation={ccwDevMax10m}
                            runwayAngle={runwayAngle * 10 + (angleSource === 'true' ? magneticDeclination : 0)}
                            headwindSpeed={headwindSpeed}
                            headwindAngle={headWindAngle}
                            crosswindSpeed={crosswindSpeed}
                            crosswindAngle={crosswindAngle}
                            mode={mode}
                            height={displayTable ? height - (mode === 'cozy' ? 328 : 288) : height - 8}
                            width={width}
                            theme={{ ...theme, themeName }}
                        />
                    </div>
                </div>
            )}

            {displayTable && !isLoading && (
                <div>
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}>LOCATION</HeaderCell>
                        <TableCell mode={settings.mode}>{locationLabel}</TableCell>
                    </div>
                    <Hr />
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}>CROSSWIND</HeaderCell>
                        <TableCell mode={settings.mode}>{crosswindSpeed !== undefined ? crosswindSpeed + ` ${units}` : 'n/a'}</TableCell>
                    </div>
                    <Hr />
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}>HEADWIND/TAILWIND</HeaderCell>
                        <TableCell mode={settings.mode}>{headwindSpeed !== undefined ? headwindSpeed + ` ${units}` : 'n/a'}</TableCell>
                    </div>
                    <Hr />
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}>DIRECTION 1MIN AVG</HeaderCell>
                        <TableCell mode={settings.mode}>{avg1m !== undefined ? `${avg1m}° ${convertDirectionToCardinal(avg1m)}` : 'n/a'}</TableCell>
                    </div>
                    <Hr />
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}>DIRECTION 10MIN AVG</HeaderCell>
                        <TableCell mode={settings.mode}>{avg10m !== undefined ? `${avg10m}° ${convertDirectionToCardinal(avg10m)}` : 'n/a'}</TableCell>
                    </div>
                    <Hr />
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}>DIRECTION 10MIN VARIANCE</HeaderCell>
                        <TableCell mode={settings.mode}>
                            {cwDevMax10m !== undefined && ccwDevMax10m !== undefined ? `CCW ${ccwDevMax10m}° - CW ${cwDevMax10m}°` : 'n/a'}
                        </TableCell>
                    </div>
                </div>
            )}
        </div>
    );
};

const HeaderCell = styled.div<{ mode?: 'cozy' | 'compact' }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 12px 0;
    white-space: nowrap;
    width: 100px;
    text-align: end;
    ${({ mode }) => TextStyles[mode ?? 'cozy'].h4}
    &:first-child {
        text-align: start;
    }
    &:last-child {
        border-bottom: none;
    }
`;

const TableCell = styled.div<{ mode?: 'cozy' | 'compact' }>`
    font-weight: 400;
    font-size: 14px;
    padding: 12px 0px 12px 0px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    white-space: nowrap;
    width: fit-content;
    text-align: end;
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p}

    &:first-child {
        text-align: start;
        font-weight: 700;
        font-size: 12px;
    }
    &:last-child {
        border-bottom: none;
    }
`;

const Hr = styled.hr`
    border: none;
    border-bottom: 1px solid #cdcdcd;
    margin: 0px;
`;
